import { useCallback, useEffect, useRef, useState } from 'react'

import type { AiMode } from 'types/search-page'

export type { AiMode }

// Shared by the /search page, Browse's own prompt toggle and the catalog
// header, so each just passes in wherever it keeps its own aiMode.
export function useAiSearchToggle(
  aiMode: AiMode,
  setAiMode: (mode: AiMode) => void,
  // Browse handles closing itself: it restores the mode from before opening and clears its search on an empty prompt.
  onCloseOverlay?: (prompt: string) => void,
) {
  const [aiPrompt, setAiPrompt] = useState('')
  const rootRef = useRef<HTMLDivElement>(null)
  const promptRef = useRef(aiPrompt)

  // Keep prompt ref in sync
  useEffect(() => {
    promptRef.current = aiPrompt
  }, [aiPrompt])

  // Close overlay but keep toggle active if there's text
  const closeOverlay = useCallback(() => {
    if (onCloseOverlay) {
      onCloseOverlay(promptRef.current)
      return
    }
    const hasText = promptRef.current.trim().length > 0
    setAiMode(hasText ? 'active' : 'off')
  }, [setAiMode, onCloseOverlay])

  const clearPrompt = useCallback(() => {
    setAiPrompt('')
  }, [])

  useEffect(() => {
    if (aiMode !== 'overlay') return

    const handleClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        closeOverlay()
      }
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeOverlay()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [aiMode, closeOverlay])

  return {
    aiMode,
    aiPrompt,
    setAiMode,
    setAiPrompt,
    clearPrompt,
    closeOverlay,
    rootRef,
  }
}
